import axios from 'axios';

// Set base URL for API calls
// In production, use /api prefix so Nginx can proxy to backend
// In development, use localhost
const isDevelopment = process.env.NODE_ENV === 'development' ||
                      window.location.hostname === 'localhost' ||
                      window.location.hostname === '127.0.0.1';

axios.defaults.baseURL = isDevelopment
  ? 'http://localhost:5000'
  : '/api'; // Production: Nginx proxies /api/* to backend

console.log('🔗 API Base URL:', axios.defaults.baseURL, '(Development:', isDevelopment + ')');

// Auth API
export const authAPI = {
  register: (data) => axios.post('/auth/register', data),
  login: (data) => axios.post('/auth/login', data),
  getProfile: () => axios.get('/auth/profile'),
  updateProfile: (data) => axios.put('/auth/profile', data),
  uploadProfilePicture: (data) => axios.post('/auth/upload-profile-picture', data, {
    headers: { 'Content-Type': 'multipart/form-data' }
  })
};

// Personas API
export const personasAPI = {
  getAll: () => axios.get('/personas'),
  getFriendPersonas: (friendId) => axios.get(`/personas/friend/${friendId}`),
  getUserProfile: (userId) => axios.get(`/personas/profile/${userId}`),
  getById: (id) => axios.get(`/personas/${id}`),
  create: (data) => axios.post('/personas', data),
  update: (id, data) => axios.put(`/personas/${id}`, data),
  delete: (id) => axios.delete(`/personas/${id}`),
  toggleSharing: (id, isShared) => axios.put(`/personas/${id}/sharing`, { is_shared: isShared }),
  cleanupTemporary: () => axios.post('/personas/cleanup-temporary'),
  previewCleanup: () => axios.get('/personas/cleanup-preview')
};

// Persona Privacy API
export const personaPrivacyAPI = {
  // Privacy settings
  getSettings: () => axios.get('/persona-privacy/settings'),
  updateSettings: (data) => axios.put('/persona-privacy/settings', data),

  // Trusted circles (Pro)
  getTrustedCircle: () => axios.get('/persona-privacy/trusted-circle'),
  addToTrustedCircle: (friendId) => axios.post(`/persona-privacy/trusted-circle/${friendId}`),
  removeFromTrustedCircle: (friendId) => axios.delete(`/persona-privacy/trusted-circle/${friendId}`),

  // Per-friend overrides (Pro)
  getOverrides: () => axios.get('/persona-privacy/overrides'),
  updateOverride: (friendId, data) => axios.put(`/persona-privacy/overrides/${friendId}`, data),

  // Suggestions
  createSuggestion: (data) => axios.post('/persona-privacy/suggestions', data),
  getReceivedSuggestions: () => axios.get('/persona-privacy/suggestions/received'),
  getSentSuggestions: () => axios.get('/persona-privacy/suggestions/sent'),
  reviewSuggestion: (suggestionId, action) => axios.put(`/persona-privacy/suggestions/${suggestionId}`, { action }),

  // Persona copying
  copyPersona: (personaId) => axios.post(`/persona-privacy/copy/${personaId}`),

  // History and rollback (Pro)
  getPersonaHistory: (personaId) => axios.get(`/persona-privacy/history/${personaId}`),
  rollbackPersona: (personaId, versionNumber) => axios.post(`/persona-privacy/rollback/${personaId}`, { version_number: versionNumber })
};

// Preferences API
export const preferencesAPI = {
  create: (data) => axios.post('/preferences', data),
  getForSubject: (subjectType, subjectId) =>
    axios.get(`/preferences/${subjectType}/${subjectId}`, subjectId === 'current' ? { headers: { 'x-current-user': 'true' } } : {}),
  update: (id, data) => axios.put(`/preferences/${id}`, data),
  delete: (id) => axios.delete(`/preferences/${id}`),
  getSummary: (subjectType, subjectId) =>
    axios.get(`/preferences/summary/${subjectType}/${subjectId}`)
};

// Friends API
export const friendsAPI = {
  // sendRequest accepts either a numeric user id or an email string.
  // If an email is provided, it will POST { friend_email } so the backend
  // can resolve it to a user id.
  sendRequest: (friendIdOrEmail) => {
    if (typeof friendIdOrEmail === 'string' && friendIdOrEmail.includes('@')) {
      return axios.post('/friends', { friend_email: friendIdOrEmail });
    }
    return axios.post('/friends', { friend_id: friendIdOrEmail });
  },
  getFriends: () => axios.get('/friends'),
  getRequests: () => axios.get('/friends/requests'),
  respondToRequest: (requestId, action) =>
    axios.put(`/friends/${requestId}`, { action }),
  removeFriendship: (friendId) => axios.delete(`/friends/${friendId}`)
};

// Recommendations API
export const recommendationsAPI = {
  getRecommendations: (data) => {
    // If using mock products, use mock recommendations instead
    if (USE_MOCK_PRODUCTS) {
      // If persona is selected, get persona-specific recommendations
      if (data.subject_type === 'persona') {
        // For persona recommendations, use a broader search that matches persona interests
        // rather than trying to match "gifts for [name]" literally
        return axios.post('/products/mock/persona-recommendations', {
          personaId: data.subject_id,
          query: data.query, // Pass along for context
          filters: data.filters || {},
          limit: 20
        });
      }
      // Otherwise, use regular mock search
      return axios.post('/products/mock/search', {
        query: data.query || 'general gifts',
        category: data.filters?.category,
        brand: data.filters?.brand,
        minPrice: data.filters?.minPrice,
        maxPrice: data.filters?.maxPrice,
        limit: 20
      });
    }
    return axios.post('/recommend', data);
  }
};

// Products API - Supports both real and mock endpoints
const USE_MOCK_PRODUCTS = process.env.REACT_APP_USE_MOCK_PRODUCTS === 'true' ||
                         window.location.search.includes('mock=true');

export const productsAPI = {
  getAll: (params) => {
    const endpoint = USE_MOCK_PRODUCTS ? '/products/mock' : '/products';
    return axios.get(endpoint, { params });
  },
  getMetadata: () => {
    const endpoint = USE_MOCK_PRODUCTS ? '/products/mock/metadata' : '/products/metadata';
    return axios.get(endpoint);
  },
  getById: (id) => {
    const endpoint = USE_MOCK_PRODUCTS ? `/products/mock/${id}` : `/products/${id}`;
    return axios.get(endpoint);
  },
  search: (data) => {
    const endpoint = USE_MOCK_PRODUCTS ? '/products/mock/search' : '/products/search';
    return axios.post(endpoint, data);
  },
  searchEbay: (data) => {
    // eBay search doesn't have a mock version, always use real endpoint
    return axios.post('/products/search-ebay', data);
  },

  // Mock-specific endpoints
  generateMockProducts: (data) => axios.post('/products/mock/generate', data),
  getPersonaProducts: (personaType, params) => axios.get(`/products/mock/persona/${personaType}`, { params }),

  // Utility to check if using mock data
  isUsingMock: () => USE_MOCK_PRODUCTS
};

// Groups API
export const groupsAPI = {
  getAll: () => axios.get('/groups'),
  getDiscover: () => axios.get('/groups/discover'),
  create: (data) => axios.post('/groups', data),
  getById: (id) => axios.get(`/groups/${id}`),
  update: (id, data) => axios.put(`/groups/${id}`, data),
  invite: (id, data) => axios.post(`/groups/${id}/invite`, data),
  respond: (id, data) => axios.post(`/groups/${id}/respond`, data),
  sharePersona: (id, data) => axios.post(`/groups/${id}/share-persona`, data),
  removeMember: (groupId, userId) => axios.delete(`/groups/${groupId}/members/${userId}`),
  contribute: (groupId, data) => axios.post(`/groups/${groupId}/contribute`, data),
  getContributions: (groupId) => axios.get(`/groups/${groupId}/contributions`),
  removeContribution: (groupId, contributionId) => axios.delete(`/groups/${groupId}/contributions/${contributionId}`),
  getGroupRecommendations: (groupId, params) => axios.get(`/groups/${groupId}/recommendations`, { params })
};

// Wish Lists API
export const wishListsAPI = {
  getAll: () => axios.get('/wishlists'),
  getShared: () => axios.get('/wishlists/shared'),
  getPublic: (params) => axios.get('/wishlists/public', { params }),
  create: (data) => axios.post('/wishlists', data),
  getById: (id) => axios.get(`/wishlists/${id}`),
  update: (id, data) => axios.put(`/wishlists/${id}`, data),
  delete: (id) => axios.delete(`/wishlists/${id}`),
  addItem: (wishListId, data) => axios.post(`/wishlists/${wishListId}/items`, data),
  removeItem: (wishListId, itemId) => axios.delete(`/wishlists/${wishListId}/items/${itemId}`),
  markPurchased: (wishListId, itemId) => axios.put(`/wishlists/${wishListId}/items/${itemId}/purchase`),
  share: (wishListId, data) => axios.post(`/wishlists/${wishListId}/share`, data)
};

// Interest Embeddings API - Semantic similarity for recommendations
export const interestEmbeddingsAPI = {
  getAll: (params) => axios.get('/interest-embeddings', { params }),
  getByCategoryTag: (category, tag) => axios.get(`/interest-embeddings/${category}/${tag}`),
  createOrUpdate: (category, tag, data) => axios.post(`/interest-embeddings/${category}/${tag}`, data),
  findSimilar: (category, tag, params) => axios.get(`/interest-embeddings/similarity/${category}/${tag}`, { params }),
  getPopular: (category, params) => axios.get('/interest-embeddings/popular', { params: { ...params, category } }),
  updatePopularity: (category, tag, data) => axios.put(`/interest-embeddings/${category}/${tag}/popularity`, data),
  deleteEmbedding: (category, tag) => axios.delete(`/interest-embeddings/${category}/${tag}`)
};

// Accounts API - Funding system
export const accountsAPI = {
  getBalance: () => axios.get('/accounts/balance'),
  getTransactions: (params) => axios.get('/accounts/transactions', { params }),
  createCheckoutSession: (data) => axios.post('/accounts/create-checkout-session', data),
  createPaymentIntent: (data) => axios.post('/accounts/create-payment-intent', data),
  fund: (data) => axios.post('/accounts/fund', data),
  allocate: (data) => axios.post('/accounts/allocate', data),
  getAllocations: () => axios.get('/accounts/allocations'),
  reallocate: (data) => axios.post('/accounts/reallocate', data),
  processGroupRefunds: (data) => axios.post('/accounts/process-group-refunds', data)
};

// AI Concierge API
export const aiConciergeAPI = {
  chat: (data) => axios.post('/ai-concierge/chat', data),
  suggest: (data) => axios.post('/ai-concierge/suggest', data)
};

const api = {
  auth: authAPI,
  personas: personasAPI,
  preferences: preferencesAPI,
  friends: friendsAPI,
  recommendations: recommendationsAPI,
  products: productsAPI,
  groups: groupsAPI,
  wishLists: wishListsAPI,
  interestEmbeddings: interestEmbeddingsAPI,
  accounts: accountsAPI,
  aiConcierge: aiConciergeAPI
};

export default api;
